/**
 * Filter bar component for AI Block activity view with multi-select dropdowns.
 */

import React, { useMemo } from 'react'
import classNames from 'classnames'
import { Button, Menu, MenuItem, MenuDivider, Popover, Icon } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import type { ExtendedBlockEvent } from './ai-block-activity-types'
import type { TokenUsage } from 'shared-libs/generated/server-types/entity/blocks/aiBlockUsageEntity'
import {
  FILTER_CONFIGS,
  generateFilterOptions,
  getActiveFilterCount,
  type IActiveFilters,
  type IFilterConfig,
  type FilterValue,
} from './ai-block-activity-filters'

interface IAiBlockActivityFilterBarProps extends IBaseProps {
  executions: ExtendedBlockEvent[]
  activeFilters: IActiveFilters
  onFiltersChange: (filters: IActiveFilters) => void
  totalTokenUsage?: TokenUsage
}

export const AiBlockActivityFilterBar: React.FC<IAiBlockActivityFilterBarProps> = ({
  className,
  executions,
  activeFilters,
  onFiltersChange,
  totalTokenUsage,
}) => {
  const filterConfigs = FILTER_CONFIGS

  // Generate options for each filter based on execution data
  const filterOptionsMap = useMemo(() => {
    const map = new Map<string, ReturnType<typeof generateFilterOptions>>()
    filterConfigs.forEach((config) => {
      map.set(config.id, generateFilterOptions(executions, config.id, config.getValueFromExecution))
    })
    return map
  }, [executions, filterConfigs])

  const handleToggleFilterValue = (filterId: string, value: FilterValue) => {
    const currentValues = activeFilters[filterId] || []
    const newValues = currentValues.includes(value)
      ? currentValues.filter((v) => v !== value)
      : [...currentValues, value]

    onFiltersChange({
      ...activeFilters,
      [filterId]: newValues,
    })
  }

  const handleClearFilter = (filterId: string) => {
    onFiltersChange({
      ...activeFilters,
      [filterId]: [],
    })
  }

  const handleClearAllFilters = () => {
    onFiltersChange({})
  }

  const activeFilterCount = getActiveFilterCount(activeFilters)

  return (
    <div className={classNames('c-aiBlockResults-filterBar', className)}>
      {totalTokenUsage && totalTokenUsage.total > 0 && (
        <div className='c-aiBlockResults-filterBar-tokenSummary'>
          <Icon icon={IconNames.TRENDING_UP} iconSize={12} />
          <span className='c-aiBlockResults-filterBar-tokenText'>
            Total: {totalTokenUsage.total.toLocaleString()} tokens
            <span className='c-aiBlockResults-filterBar-tokenBreakdown'>
              ({totalTokenUsage.input?.toLocaleString()} in / {totalTokenUsage.output?.toLocaleString()} out)
            </span>
          </span>
        </div>
      )}

      <div className='c-aiBlockResults-filterBar-filters'>
        {filterConfigs.map((config) => (
          <FilterDropdown
            key={config.id}
            config={config}
            options={filterOptionsMap.get(config.id) || []}
            selectedValues={activeFilters[config.id] || []}
            onToggleValue={(value) => handleToggleFilterValue(config.id, value)}
            onClear={() => handleClearFilter(config.id)}
          />
        ))}
      </div>

      {activeFilterCount > 0 && (
        <Button
          minimal
          small
          icon={IconNames.CROSS}
          text='Clear all'
          onClick={handleClearAllFilters}
          className='c-aiBlockResults-filterBar-clearAll'
        />
      )}
    </div>
  )
}

interface IFilterDropdownProps {
  config: IFilterConfig
  options: ReturnType<typeof generateFilterOptions>
  selectedValues: FilterValue[]
  onToggleValue: (value: FilterValue) => void
  onClear: () => void
}

const FilterDropdown: React.FC<IFilterDropdownProps> = ({
  config,
  options,
  selectedValues,
  onToggleValue,
  onClear,
}) => {
  const selectedCount = selectedValues.length

  // Get labels for selected values
  const selectedLabels = selectedValues
    .map((value) => options.find((opt) => opt.value === value)?.label)
    .filter(Boolean) as string[]

  // Format selected values for display
  const getButtonText = (): string => {
    if (selectedCount === 0) {
      return config.label
    }
    if (selectedCount === 1) {
      return selectedLabels[0]
    }
    // For multiple selections, show first value + count
    return `${selectedLabels[0]} +${selectedCount - 1}`
  }

  const menu = (
    <Menu className='c-aiBlockResults-filterMenu'>
      {options.length === 0 ? (
        <MenuItem disabled text='No options available' />
      ) : (
        options.map((option) => {
          const isSelected = selectedValues.includes(option.value)
          return (
            <MenuItem
              key={String(option.value)}
              text={
                <div className='c-aiBlockResults-filterMenuItem'>
                  <span className='c-aiBlockResults-filterMenuItem-label'>{option.label}</span>
                  {option.count != null && (
                    <span className='c-aiBlockResults-filterMenuItem-count'>{option.count}</span>
                  )}
                </div>
              }
              icon={isSelected ? IconNames.TICK : IconNames.BLANK}
              onClick={() => onToggleValue(option.value)}
              shouldDismissPopover={false}
            />
          )
        })
      )}
      {selectedCount > 0 && (
        <>
          <MenuDivider />
          <MenuItem icon={IconNames.CROSS} text='Clear selection' onClick={onClear} />
        </>
      )}
    </Menu>
  )

  return (
    <Popover content={menu} placement='bottom-start' minimal>
      <Button
        small
        rightIcon={IconNames.CARET_DOWN}
        className={classNames('c-aiBlockResults-filterButton', {
          'c-aiBlockResults-filterButton--active': selectedCount > 0,
        })}
      >
        <span className='c-aiBlockResults-filterButton-label'>{getButtonText()}</span>
      </Button>
    </Popover>
  )
}
